import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SocialMetaTags from './SocialMetaTags';
import { apiFetch, getApiHost } from '../utils/api';
import { confChip, linkProvenance, evidenceTitle } from '../utils/walletLinkDisplay';

interface FigureData {
  figureKey: string;
  displayName: string;
  aliases?: Array<{ handle: string; status?: string; followers?: number | null }>;
  pseudonymous: boolean;
  xHandle: string | null;
  publicStature: string | null;
  statureEvidence: string | null;
  publicSentiment: string | null;
  sentimentSummary: string | null;
  // Agent-authored public "who is this figure" text — preferred over
  // reputationSummary, which is really internal background/trust context.
  summary: string | null;
  reputationSummary: string | null;
  notableControversies: string[];
  trustLevel: string | null;
  sources: string[];
  wallets: string[];
  walletLinks?: Array<{
    address: string;
    confidence: 'confirmed' | 'probable' | 'claimed';
    status?: string;
    evidence: Array<{ type: string; addedBy: string; confidence?: string; source?: string | null; basis?: string | null; vendor?: string | null; vendorValue?: string | null; addedAt?: string | null }>;
    mitigates?: boolean | null;
  }>;
  walletCountByConfidence?: { confirmed: number; probable: number; claimed: number };
  mitigates: boolean;
  tokens: Array<{ mint: string; symbol: string; percentage: number; safetyLevel: string | null }>;
  associatedTokens?: Array<{
    mint: string;
    symbol: string | null;
    relationships: Array<{ relationship: string; via: string; note?: string | null; percentage?: number }>;
    safetyLevel: string | null;
    isCopycat?: boolean;
    copycatOf?: string | null;
    createdAt?: number | null; // token creation time (ms) — set only for tokens this figure created
  }>;
  associatedTokensCapped?: boolean; // true when the creator-tokens list hit the 100 cap (true total unknown)
  agentNotes?: Array<{ note: string; at: string | null }>;
  researchedAt: string | null;
}

// Relationship-kind badge colors (creator/owner derived on-chain; promoter/
// admin/insider are curated). Keep in sync with the MCP's TOKEN_RELATIONSHIPS.
const relColor = (r: string): string => {
  switch (r) {
    case 'creator': return 'text-crypto-accent border-crypto-accent/40 bg-crypto-accent/10';
    case 'owner': return 'text-blue-400 border-blue-500/40 bg-blue-500/10';
    case 'recipient': return 'text-amber-400 border-amber-500/40 bg-amber-500/10';
    case 'promoter': return 'text-pink-400 border-pink-500/40 bg-pink-500/10';
    case 'admin': return 'text-teal-400 border-teal-500/40 bg-teal-500/10';
    case 'insider': return 'text-orange-400 border-orange-500/40 bg-orange-500/10';
    default: return 'text-gray-400 border-gray-600 bg-gray-500/10';
  }
};
const SAFETY_COLORS: Record<string, string> = {
  DANGEROUS: 'bg-red-500/20 text-red-400 border-red-500/30',
  RISKY: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  OK: 'bg-green-500/20 text-green-400 border-green-500/30',
  SAFE: 'bg-green-500/20 text-green-400 border-green-500/30',
};

// X profile photo via unavatar (no auth). Falls back to the figure's initial if
// there's no handle or the avatar can't be resolved. The X avatar is public and
// safe to show even for pseudonymous figures (it's their chosen public image).
const FigureAvatar: React.FC<{ handle: string | null; displayName: string }> = ({ handle, displayName }) => {
  const [failed, setFailed] = useState(false);
  if (handle && !failed) {
    return (
      <img
        src={`https://unavatar.io/twitter/${encodeURIComponent(handle)}?fallback=false`}
        alt={displayName}
        onError={() => setFailed(true)}
        className="w-16 h-16 rounded-full object-cover border-2 border-crypto-green/40 flex-shrink-0 bg-crypto-dark"
      />
    );
  }
  return (
    <div className="w-16 h-16 rounded-full bg-crypto-green/15 border-2 border-crypto-green/30 flex items-center justify-center flex-shrink-0">
      <span className="text-2xl font-bold text-crypto-green">{(displayName || '?').charAt(0).toUpperCase()}</span>
    </div>
  );
};

const FigurePage: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const [figure, setFigure] = useState<FigureData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`${getApiHost()}/api/figures/${key}`);
        if (res.status === 404) { if (!cancelled) { setError('notfound'); setLoading(false); } return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setFigure(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load figure');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <SocialMetaTags
        title={figure ? `${figure.displayName} — deepnets` : 'Figure — deepnets'}
        description={figure?.summary || figure?.reputationSummary || 'Known-figure profile on deepnets.ai'}
        image={`${window.location.origin}/logo192.png`}
        url={`${window.location.origin}/figures/${key}`}
        type="article"
      />
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
        {loading && <div className="text-gray-400 py-16 text-center">Loading figure…</div>}

        {error === 'notfound' && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🕵️</div>
            <h1 className="text-2xl font-bold mb-2">Figure not found</h1>
            <p className="text-gray-400">No researched figure matches <span className="font-mono">{key}</span>.</p>
            <Link to="/" className="text-crypto-accent hover:underline mt-4 inline-block">← Back home</Link>
          </div>
        )}
        {error && error !== 'notfound' && (
          <div className="text-crypto-red py-16 text-center">Failed to load: {error}</div>
        )}

        {figure && !loading && (
          <>
            <Link to="/figures" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-4">
              <i className="fas fa-arrow-left text-xs" /> All figures
            </Link>
            {/* Hero */}
            <div className="flex items-start gap-4 mb-6">
              <FigureAvatar handle={figure.xHandle} displayName={figure.displayName} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* displayName is the main name we refer to the figure by; no
                      "pseudonym"/"alias" label on it. Additional handles live in aliases[]. */}
                  <h1 className="text-3xl font-bold">{figure.displayName}</h1>
                </div>
                {figure.xHandle && (
                  <a href={`https://x.com/${figure.xHandle}`} target="_blank" rel="noopener noreferrer" className="text-crypto-accent hover:underline text-sm mt-1 inline-flex items-center gap-1">
                    <i className="fab fa-x-twitter text-xs" />@{figure.xHandle}
                  </a>
                )}
                {/* Other handles this figure uses (alt/dead/suspended/renamed) —
                    all resolve to the same figure. Live ones link to X; dead ones don't. */}
                {figure.aliases && figure.aliases.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>also:</span>
                    {figure.aliases.map(a => {
                      const inactive = a.status === 'dead' || a.status === 'suspended';
                      const label = <>@{a.handle}{a.status && a.status !== 'active' ? <span className="text-gray-600"> · {a.status}</span> : null}</>;
                      return inactive
                        ? <span key={a.handle} className="text-gray-500 line-through decoration-gray-700">{label}</span>
                        : <a key={a.handle} href={`https://x.com/${a.handle}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-crypto-accent hover:underline">{label}</a>;
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Trust / stature / sentiment are kept internal for now — not shown. */}

            {/* About — the agent-authored `summary` is the public write-up. Falls back to
                reputationSummary (internal background context) for figures the agent
                hasn't summarized yet, so those pages don't render blank. */}
            {(figure.summary || figure.reputationSummary) && (
              <section className="bg-crypto-card border border-gray-800 rounded-xl p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">About</h2>
                {/* whitespace-pre-line so the summary's paragraph breaks survive */}
                <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-line">{figure.summary || figure.reputationSummary}</p>
              </section>
            )}

            {/* Controversies */}
            {figure.notableControversies?.length > 0 && (
              <section className="bg-crypto-card border border-gray-800 rounded-xl p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Notable controversies</h2>
                <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                  {figure.notableControversies.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </section>
            )}

            {/* Associated tokens — each tagged with its relationship KIND.
                creator/owner are derived on-chain; promoter/admin/insider are
                curated. Every chip links to the token page. */}
            {figure.associatedTokens && figure.associatedTokens.length > 0 && (() => {
              // One bubble per token (mint), all its relationship kinds as badges.
              // Genuine first, copycats last; richer kinds before owner-only.
              const ownerOnly = (t: typeof figure.associatedTokens[number]) => t.relationships.every(r => r.relationship === 'owner');
              // Tokens the figure created lead, most-recently-created first. Everything
              // else keeps the prior preference (genuine before copycats, richer kinds
              // before owner-only).
              const sorted = [...figure.associatedTokens].sort((a, b) => {
                const ca = a.createdAt ?? null, cb = b.createdAt ?? null;
                if (ca != null && cb != null) return cb - ca;
                if ((ca != null) !== (cb != null)) return ca != null ? -1 : 1;
                return Number(!!a.isCopycat) - Number(!!b.isCopycat) || Number(ownerOnly(a)) - Number(ownerOnly(b));
              });
              const shown = sorted.slice(0, 40);
              return (
                <section className="bg-crypto-card border border-gray-800 rounded-xl p-4 mb-4">
                  <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Associated tokens ({figure.associatedTokensCapped ? '100+' : figure.associatedTokens.length})</h2>
                  <div className="flex flex-wrap gap-2">
                    {shown.map(t => (
                      <Link
                        key={t.mint}
                        to={`/token/${t.mint}`}
                        title={t.isCopycat && t.copycatOf ? `Copycat of ${t.copycatOf}` : undefined}
                        className={`flex items-center gap-1.5 bg-crypto-dark border rounded-full pl-2 pr-2.5 py-1 transition ${t.isCopycat ? 'border-red-500/40 opacity-70 hover:opacity-100' : 'border-gray-700 hover:border-crypto-accent/60'}`}
                      >
                        <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center text-[10px] text-gray-300 flex-shrink-0">{(t.symbol || '?').charAt(0)}</div>
                        <span className="text-sm font-semibold text-white">${t.symbol || t.mint.slice(0, 4)}</span>
                        {t.isCopycat && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-red-500/20 text-red-400 border-red-500/40">copycat</span>
                        )}
                        {t.relationships.map(r => (
                          <span key={r.relationship} className={`text-[10px] capitalize px-1.5 py-0.5 rounded border ${relColor(r.relationship)}`}>{r.relationship}</span>
                        ))}
                        {t.safetyLevel && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${SAFETY_COLORS[t.safetyLevel] ?? 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{t.safetyLevel}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                  {figure.associatedTokens.length > shown.length && (
                    <p className="text-[11px] text-gray-500 mt-2">+{figure.associatedTokens.length - shown.length} more</p>
                  )}
                </section>
              );
            })()}

            {/* Linked wallets — EVERY linkage, each with its confidence + source.
                Confirmed = attribution; probable/claimed = tracked leads (e.g. a
                GMGN vendor label), shown so connections aren't missed. */}
            {(figure.walletLinks?.length || figure.wallets?.length) > 0 && (
              <section className="bg-crypto-card border border-gray-800 rounded-xl p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-1">Linked wallets ({figure.walletLinks?.length ?? figure.wallets.length})</h2>
                {figure.walletCountByConfidence && (figure.walletCountByConfidence.probable + figure.walletCountByConfidence.claimed) > 0 && (
                  <p className="text-[11px] text-gray-500 mb-3">
                    {figure.walletCountByConfidence.confirmed} confirmed · {figure.walletCountByConfidence.probable} probable · {figure.walletCountByConfidence.claimed} claimed
                  </p>
                )}
                <div className="space-y-2">
                  {figure.walletLinks?.length
                    ? figure.walletLinks.map(l => {
                        const prov = linkProvenance(l.evidence);
                        return (
                          <div key={l.address} className="flex items-center gap-2 flex-wrap">
                            <Link to={`/wallet/${l.address}`} className="font-mono text-sm text-crypto-accent hover:underline break-all">{l.address}</Link>
                            <span className={`text-[10px] px-1.5 py-px rounded border ${confChip(l.confidence)}`} title={evidenceTitle(l.evidence)}>{l.confidence}</span>
                            {prov && <span className="text-[10px] text-gray-500">via {prov}</span>}
                          </div>
                        );
                      })
                    : figure.wallets.map(w => (
                        <Link key={w} to={`/wallet/${w}`} className="block font-mono text-sm text-crypto-accent hover:underline break-all">{w}</Link>
                      ))}
                </div>
              </section>
            )}

            {/* Notes — agent-recorded research annotations (append-only). Source
                URLs are intentionally not surfaced (provenance stays internal). */}
            {figure.agentNotes && figure.agentNotes.length > 0 && (
              <section className="bg-crypto-card border border-gray-800 rounded-xl p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Notes ({figure.agentNotes.length})</h2>
                <ul className="space-y-2.5">
                  {figure.agentNotes.map((n, i) => (
                    <li key={i} className="border-l-2 border-gray-700 pl-3">
                      <p className="text-sm text-gray-300 leading-relaxed">{n.note}</p>
                      {n.at && <p className="text-[11px] text-gray-600 mt-0.5">{new Date(n.at).toLocaleDateString()}</p>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Sources intentionally not rendered — Grok's raw source list bleeds in
                unrelated/unsafe URLs from web_search. Kept in the DB, not shown. */}

            {figure.researchedAt && (
              <p className="text-xs text-gray-600 mt-2">Researched {new Date(figure.researchedAt).toLocaleDateString()}. Assessments are algorithmic and may be revised.</p>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default FigurePage;
